// Bundles the sanitized Contoso CDX tenant export from
// src/lib/demo/data/ into a single typed dataset module.
//
// JSON files are imported via Vite's static import so they're tree-
// shakable + bundled into the JS chunk (no runtime fetches). Embedded
// JSON-string columns (per data/README.md) are eagerly parsed once
// at module load.
//
// Raw row shapes are loose (snake_case + PascalCase from the C#
// backend's serialization). Higher-level demoAdapter responses
// project to whatever shape the production endpoint would return.

/* eslint-disable @typescript-eslint/no-explicit-any */

import tenantRows               from './data/tenant.json'
import driftRows                from './data/configuration-drifts.json'
import driftAuditRows           from './data/drift-audit-log.json'
import baselineRows             from './data/configuration-baselines.json'
import metricSnapshotRows       from './data/metric-snapshots.json'
import frameworkRows            from './data/compliance-frameworks.json'
import policyRows               from './data/compliance-policies.json'
import tenantStandardRows       from './data/tenant-standard-assignments.json'
import violationRows            from './data/compliance-violations.json'
import scoreHistoryRows         from './data/compliance-score-history.json'
import evaluationRows           from './data/compliance-evaluations.json'
import waiverRows               from './data/compliance-waivers.json'
import secureScoreRows          from './data/secure-score-cache.json'
import scubaReportRows          from './data/scuba-reports.json'
import scubaProductRows         from './data/scuba-product-results.json'
import userInsightsRows         from './data/user-insights-cache.json'
import userInsightsHistoryRows  from './data/user-insights-history.json'
import userDetailRows           from './data/user-detail-cache.json'
import licenseSkuRows           from './data/license-sku-cache.json'
import oauthAppRows             from './data/oauth-apps.json'
import oauthPermissionRows      from './data/oauth-app-permissions.json'
import oauthConsentRows         from './data/oauth-consent-records.json'

function parseJsonField<T = any>(s: any): T | null {
  if (s === null || s === undefined) return null
  if (typeof s !== 'string') return s as T
  try { return JSON.parse(s) as T } catch { return null }
}

// Lowercase the first character of each top-level key — DB exports are
// PascalCase but the frontend expects camelCase. Applied at the
// dataset → response boundary by adapter route handlers.
export function camel<T extends Record<string, any>>(o: T): Record<string, any> {
  if (!o || typeof o !== 'object') return o
  const out: Record<string, any> = {}
  for (const k of Object.keys(o)) {
    if (!k) continue
    out[k.charAt(0).toLowerCase() + k.slice(1)] = o[k]
  }
  return out
}

// Deep version for nested objects + arrays.
export function camelDeep(v: any): any {
  if (Array.isArray(v)) return v.map(camelDeep)
  if (v && typeof v === 'object') {
    const out: Record<string, any> = {}
    for (const k of Object.keys(v)) {
      out[k.charAt(0).toLowerCase() + k.slice(1)] = camelDeep((v as any)[k])
    }
    return out
  }
  return v
}

// Pre-parse the heaviest embedded JSON strings once so consumers
// don't pay the cost on every render.
const tenant            = (tenantRows as any[])[0] ?? null
const secureScoreRaw    = (secureScoreRows as any[])[0] ?? null
const secureScoreData   = secureScoreRaw ? parseJsonField(secureScoreRaw.ScoreDataJson) : null
const scubaReportRaw    = (scubaReportRows as any[])[0] ?? null
const scubaResults      = scubaReportRaw ? parseJsonField(scubaReportRaw.ResultsJson) : null
const userInsights      = (userInsightsRows as any[])[0] ?? null

const scubaProducts = (scubaProductRows as any[]).map(r => ({
  ...r,
  ControlResults: parseJsonField(r.ControlResultsJson),
}))

const drifts = (driftRows as any[]).map(r => ({
  ...r,
  ComplianceImpact:    parseJsonField(r.ComplianceImpactJson),
  FrameworksAffected:  parseJsonField(r.FrameworksAffectedJson),
}))

const scoreHistory = (scoreHistoryRows as any[]).map(r => ({
  ...r,
  WorkloadScores:     parseJsonField(r.WorkloadScoresJson),
  FrameworksParsed:   parseJsonField(r.frameworks_json),
}))

const userDetails = (userDetailRows as any[]).map(r => ({
  ...r,
  AssignedLicenses:        parseJsonField(r.AssignedLicensesJson) ?? [],
  MfaMethods:              parseJsonField(r.MfaMethodsJson) ?? [],
  PhishResistantMethods:   parseJsonField(r.PhishResistantMethodsJson) ?? [],
  Roles:                   parseJsonField(r.RolesJson) ?? [],
}))

const oauthApps = (oauthAppRows as any[]).map(r => ({
  ...r,
  Raw: parseJsonField(r.RawJson),
}))

export const dataset = {
  // ── Tenant ─────────────────────────────────────────────────────────
  tenant,

  // ── Baseline / drift ───────────────────────────────────────────────
  drifts,
  driftAudit:    driftAuditRows as any[],
  baselines:     baselineRows as any[],

  // ── Posture trends ─────────────────────────────────────────────────
  metricSnapshots: metricSnapshotRows as any[],

  // ── Compliance ─────────────────────────────────────────────────────
  frameworks:        frameworkRows as any[],
  policies:          policyRows as any[],
  tenantStandards:   tenantStandardRows as any[],
  violations:        violationRows as any[],
  scoreHistory,
  evaluations:       evaluationRows as any[],
  waivers:           waiverRows as any[],

  // ── Microsoft Secure Score ─────────────────────────────────────────
  secureScoreCacheRow: secureScoreRaw,
  secureScoreData,

  // ── SCuBA Deep ─────────────────────────────────────────────────────
  scubaReport:   scubaReportRaw,
  scubaResults,
  scubaProducts,

  // ── User Analytics ─────────────────────────────────────────────────
  userInsights,
  userInsightsHistory: userInsightsHistoryRows as any[],
  userDetails,
  licenseSkus:         licenseSkuRows as any[],

  // ── OAuth Apps / App Posture ───────────────────────────────────────
  oauthApps,
  oauthPermissions:    oauthPermissionRows as any[],
  oauthConsents:       oauthConsentRows as any[],
}

// ── Quick row counts (handy in console logs + sanity checks) ─────────
export const datasetCounts = {
  drifts:           drifts.length,
  baselines:        (baselineRows as any[]).length,
  violations:       (violationRows as any[]).length,
  evaluations:      (evaluationRows as any[]).length,
  policies:         (policyRows as any[]).length,
  scoreHistory:     scoreHistory.length,
  metricSnapshots:  (metricSnapshotRows as any[]).length,
  userDetails:      userDetails.length,
  oauthApps:        oauthApps.length,
  oauthPermissions: (oauthPermissionRows as any[]).length,
  oauthConsents:    (oauthConsentRows as any[]).length,
}
